import { memo, useMemo } from 'react';
import { css } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { PaginatedList } from '@frontend/components';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import {
  useGetSchedulePulseInsuranceTabAppointments,
  useGetInsuranceStatuses,
  useInsuranceTabShallowStore,
} from '@frontend/insurance-tab';
import { theme } from '@frontend/theme';
import { useSchedulePulseInsuranceTabContext } from '../../context';
import { InsuranceSpinnerLoader } from './InsuranceSpinnerLoader';
import { PatientListRow } from './PatientListRow';
import { SelectionFilterContainer } from './SelectionFilterContainer';

interface SingleLocationViewProps {
  locationId: string;
  height?: number | string;
}

export const SingleLocationView = memo(({ locationId, height }: SingleLocationViewProps) => {
  const { t } = useTranslation('schedule-pulse');
  const { selectedLocationIds, selectedDate } = useSchedulePulseInsuranceTabContext();

  const { allAppointmentsData, isLoadingAppointmentsData, isScheduleV3FlagEnabled } =
    useGetSchedulePulseInsuranceTabAppointments({
      selectedDate: selectedDate || formatDate(new Date(), 'MM/DD/YYYY'),
      locationIds: [locationId],
    });

  const { isLoading } = useGetInsuranceStatuses({
    locationId: locationId,
    appointments: allAppointmentsData || [],
    isSingleLocationView: true,
  });

  const { currentFilterValue, currentStatusSelection, insuranceStatusByPersonIdsMap } = useInsuranceTabShallowStore(
    'currentFilterValue',
    'currentStatusSelection',
    'insuranceStatusByPersonIdsMap'
  );

  const appointmentsListToRender = useMemo(() => {
    const hasFilterValues = currentStatusSelection || currentFilterValue;

    if (!allAppointmentsData?.length) return [];

    if (!hasFilterValues) return allAppointmentsData?.filter((appt) => appt?.locationId === locationId);

    return allAppointmentsData?.filter((appt) => {
      const personId = isScheduleV3FlagEnabled
        ? (appt as CalendarEvent)?.attendeeId
        : (appt as Appointment)?.person?.personId || '';

      if (!personId) return false;

      if (personId && appt?.locationId === locationId) {
        return (
          insuranceStatusByPersonIdsMap?.[personId]?.toLowerCase() === currentStatusSelection?.toLowerCase() ||
          insuranceStatusByPersonIdsMap?.[personId]?.toLowerCase() === currentFilterValue?.toLowerCase()
        );
      }
      return false;
    });
  }, [
    currentStatusSelection,
    currentFilterValue,
    allAppointmentsData,
    locationId,
    isScheduleV3FlagEnabled,
    insuranceStatusByPersonIdsMap,
  ]);

  if (isLoading || isLoadingAppointmentsData) {
    return <InsuranceSpinnerLoader />;
  }

  return (
    <>
      <div css={singleViewContainerStyles(selectedLocationIds?.length > 1)}>
        {selectedLocationIds?.length > 1 && <SelectionFilterContainer />}
        <PaginatedList
          emptyStateConfig={{
            header: t('No Appointments found'),
            type: 'schedule',
          }}
          height={typeof height === 'number' ? height - 65 : height ?? '100%'} // 65px is the height of the top section
          listData={appointmentsListToRender || []}
          renderListItem={({ listItem }) => (
            <PatientListRow
              isScheduleV3FlagEnabled={isScheduleV3FlagEnabled}
              shouldShowInsuranceStatus
              appt={listItem}
              locationId={locationId}
            />
          )}
          endOfListText=''
        />
      </div>
    </>
  );
});

SingleLocationView.displayName = 'SingleLocationView';

const singleViewContainerStyles = (isMultiLocation: boolean) =>
  css({
    background: theme.colors.white,
    flex: 1,
    ...(isMultiLocation && {
      '> div:first-child': {
        margin: theme.spacing(2, 2),
      },
    }),
  });
